/**
 * Shared CSS utilities for cell overlay rendering (corner triangles, icon positioning).
 * Used by both AiStatusCellRenderer and SbsCellRenderer.
 */

import { palette } from "@platform/design-system";

/**
 * Cell tints, taken from the palette so these constants and the themed
 * components drawing the same tints cannot drift apart. A component with a
 * theme in hand should read `palette.ai` directly; these serve module-scope
 * lookups and tests, which have no theme.
 */
export const CELL_NEEDS_REVIEW_BG = palette.ai.reviewBackground;

/** Background tint applied to cells flagged "Broker Highlight". */
export const CELL_BROKER_HIGHLIGHT_BG = "#E3F2FD";

/** Background tint applied to cells an analyst has validated. */
export const CELL_VALIDATED_BG = palette.ai.validatedBackground;

const DEFAULT_CORNER_SIZE = 9;

/** Distance from the cell edge to the first status icon. */
export const ICON_OVERLAY_INSET = 10;

/** Rendered width of one status icon glyph. */
export const ICON_OVERLAY_SIZE = 16;

/** Centre-to-centre distance between adjacent status icons. */
export const ICON_OVERLAY_SPACING = 18;

/** Breathing room between the icon cluster and the cell's text. */
const ICON_TEXT_GAP = 4;

type CornerPosition = "top-left" | "top-right" | "bottom-left" | "bottom-right";

type IconPosition = "left" | "right" | "center";

/**
 * Returns an sx object that renders a CSS border-trick triangle in the
 * specified corner of a position:relative container.
 */
export const cornerTriangleSx = (
  position: CornerPosition,
  color: string,
  size: number = DEFAULT_CORNER_SIZE,
): Record<string, unknown> => {
  const borderMap: Record<CornerPosition, Record<string, unknown>> = {
    "top-left": {
      top: 0,
      left: 0,
      borderWidth: `${size}px ${size}px 0 0`,
      borderColor: `${color} transparent transparent transparent`,
    },
    "top-right": {
      top: 0,
      right: 0,
      borderWidth: `0 ${size}px ${size}px 0`,
      borderColor: `transparent ${color} transparent transparent`,
    },
    "bottom-left": {
      bottom: 0,
      left: 0,
      borderWidth: `${size}px 0 0 ${size}px`,
      borderColor: `transparent transparent transparent ${color}`,
    },
    "bottom-right": {
      bottom: 0,
      right: 0,
      borderWidth: `0 0 ${size}px ${size}px`,
      borderColor: `transparent transparent ${color} transparent`,
    },
  };

  return {
    position: "absolute",
    width: 0,
    height: 0,
    borderStyle: "solid",
    zIndex: 1,
    ...borderMap[position],
  };
};

/**
 * Width the text has to give up on each side so a cluster of `iconCount` status
 * icons doesn't sit on top of it. Applied symmetrically by the caller, which
 * keeps wrapped text optically centred in the cell while clearing the icons.
 */
export const iconGutterWidth = (iconCount: number): number =>
  iconCount === 0
    ? 0
    : ICON_OVERLAY_INSET +
      (iconCount - 1) * ICON_OVERLAY_SPACING +
      ICON_OVERLAY_SIZE +
      ICON_TEXT_GAP;

interface IconOverlayOptions {
  /** Slot in a left-to-right cluster of icons; ignored when centred. */
  index?: number;
  fontSize?: number;
}

/**
 * Returns an sx object for absolutely positioning an icon overlay within a
 * position:relative cell container. Stacking several starts from `index: 0` and
 * stays in step with the gutter `iconGutterWidth` reserves for them.
 */
export const iconOverlaySx = (
  position: IconPosition = "left",
  { index = 0, fontSize = ICON_OVERLAY_SIZE }: IconOverlayOptions = {},
): Record<string, unknown> => {
  const clusterOffset = ICON_OVERLAY_INSET + index * ICON_OVERLAY_SPACING;
  const positionMap: Record<IconPosition, Record<string, unknown>> = {
    left: { left: clusterOffset },
    right: { right: clusterOffset },
    center: { left: "50%", transform: "translate(-50%, -50%)" },
  };

  return {
    position: "absolute",
    top: "50%",
    transform: "translateY(-50%)",
    fontSize,
    zIndex: 2,
    ...positionMap[position],
  };
};
